/**
 * substitutionEdit — the rules behind the Review tab's substitution
 * corrections.
 *
 * Client ask: "Activar en revisión que pueda modificar los jugadores que
 * entran y salen ya que si hay errores se debe corregir de inmediato por el #
 * correcto." A substitution logged against the wrong shirt number has to be
 * fixable on the spot, for BOTH directions — the review editor only ever
 * exposed the outgoing player.
 *
 * The checks below are WARNINGS, never blocks. That is a deliberate product
 * decision, not an oversight: a correction is made precisely because the log
 * is already wrong, so validating the new pick against the state derived from
 * that same wrong log can trap the operator. If an earlier substitution named
 * the wrong player, every later one looks invalid until it is fixed, and the
 * operator has to be free to fix them in whatever order they notice them.
 */
import { computeOnFieldRoster } from "../../../lib/onFieldRoster";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../types";

export type SubstitutionEditWarning =
  | "samePlayer"
  | "playerInAlreadyOnPitch"
  | "playerOutNotOnPitch"
  | "playerInExpelled"
  | "playerOutExpelled";

export interface SubstitutionEditCheck {
  playerOffId?: string | null;
  playerOnId?: string | null;
  /**
   * Who was on the pitch immediately BEFORE this substitution — see
   * `onFieldIdsBeforeEvent`. Omit when it cannot be derived (no roster yet);
   * the pitch-membership checks are then skipped rather than guessed at.
   */
  onFieldIdsBefore?: ReadonlySet<string> | null;
  expelledPlayerIds?: ReadonlySet<string> | null;
}

export function validateSubstitutionEdit({
  playerOffId,
  playerOnId,
  onFieldIdsBefore,
  expelledPlayerIds,
}: SubstitutionEditCheck): SubstitutionEditWarning[] {
  const warnings: SubstitutionEditWarning[] = [];
  const off = playerOffId || "";
  const on = playerOnId || "";

  if (off && on && off === on) {
    // Everything downstream would fire too, and all of it would be noise
    // next to the one thing actually wrong.
    return ["samePlayer"];
  }

  if (onFieldIdsBefore) {
    if (on && onFieldIdsBefore.has(on)) warnings.push("playerInAlreadyOnPitch");
    if (off && !onFieldIdsBefore.has(off)) warnings.push("playerOutNotOnPitch");
  }

  if (expelledPlayerIds) {
    if (on && expelledPlayerIds.has(on)) warnings.push("playerInExpelled");
    if (off && expelledPlayerIds.has(off)) warnings.push("playerOutExpelled");
  }

  return warnings;
}

/**
 * The set of player ids on the pitch immediately before `event`.
 *
 * Replays only the events that precede it, so editing an early substitution
 * is checked against the state at that moment rather than against the end of
 * the match. Returns null when there is nothing to derive from, which the
 * caller reads as "skip the pitch checks".
 *
 * Reuses `computeOnFieldRoster` rather than walking substitutions again — a
 * second walker would be a second, silently diverging opinion about who is
 * playing (the same reasoning that put that function in `lib/` in the first
 * place).
 */
export function onFieldIdsBeforeEvent(
  match: Match | null,
  events: MatchEvent[],
  eventIndex: number,
): ReadonlySet<string> | null {
  if (!match || eventIndex < 0) return null;
  const roster = computeOnFieldRoster(match, events.slice(0, eventIndex));
  const ids = new Set<string>();
  for (const p of roster.home) ids.add(p.id);
  for (const p of roster.away) ids.add(p.id);
  // An empty set means "no lineup data", not "nobody is playing" — telling
  // the operator all eleven of their picks are wrong would be worse than
  // saying nothing.
  return ids.size > 0 ? ids : null;
}
